import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AlertIcon from './AlertIcon';
import AlertActions from './AlertActions';
import {
  AlertRoot,
  AlertBody,
  AlertMessageRoot,
  AlertLayout,
} from './Alert.style';
import FooterActions from './FooterActions';

const Alert = ({
  type,
  layout,
  actions,
  children,
  visibleDuration,
  onClose,
  onDismiss,
  onTrigger,
  triggerText,
}) => {
  const [isHidden, setHiddenState] = useState(false);
  const handleClose = () => setHiddenState(true) && onClose();
  const handleDismiss = () => setHiddenState(true) && onDismiss();

  useEffect(() => {
    if (!visibleDuration) {
      return;
    }
    const timeout = setTimeout(() => setHiddenState(true), visibleDuration);

    return () => clearTimeout(timeout);
  }, [visibleDuration]);

  if (isHidden) {
    return null;
  }

  return (
    <AlertRoot role="alert" type={type}>
      <AlertBody>
        <AlertLayout type={layout}>
          <AlertIcon type={type} />
          <AlertMessageRoot>{children}</AlertMessageRoot>
        </AlertLayout>
        <AlertActions onClose={handleClose} closeActionVisible={!!onClose}>
          {actions}
        </AlertActions>
      </AlertBody>
      {layout !== 'vertical' && (
        <FooterActions
          onDismiss={handleDismiss}
          dismissVisible={!!onDismiss}
          onTrigger={onTrigger}
          triggerVisible={!!onTrigger}
          triggerText={triggerText}
        />
      )}
    </AlertRoot>
  );
};

Alert.propTypes = {
  /** Add custom actions */
  actions: PropTypes.node,

  /** The content of the Alert */
  children: PropTypes.node.isRequired,

  /** Callback for the default close action */
  onClose: PropTypes.func,

  /** Callback for the dismiss action */
  onDismiss: PropTypes.func,

  /** Callback for the default close action */
  onTrigger: PropTypes.func,

  triggerText: PropTypes.string,

  /** Automatically hide alert after X ms */
  visibleDuration: PropTypes.number,

  /** The type of the layout */
  layout: PropTypes.oneOf(['horizontal', 'vertical']),

  /** The type that defines the default icon and styles */
  type: PropTypes.oneOf([
    'custom',
    'error',
    'info',
    'success',
    'warning',
    'loading',
    'announcement',
  ]),
};

Alert.defaultProps = {
  type: 'custom',
  layout: 'horizontal',
};

export default Alert;
